import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Instagram, Facebook, Twitter, Clock } from 'lucide-react';
import Header from './components/Header11';
import Footer from './components/Footer11';
import Button from './components/Button';

const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
    });
    const [submitted, setSubmitted] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        console.log('Contact form submitted:', formData);
        setSubmitted(true);
        setTimeout(() => {
            setSubmitted(false);
            setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
        }, 3000);
    };

    return (
        <div className="min-h-screen bg-white">
            <Header showBackButton={true} cartCount={3} />

            {/* Hero Section */}
            <section className="bg-gray-900 text-white py-16 lg:py-24">
                <div className="container mx-auto px-4 lg:px-8">
                    <div className="max-w-3xl mx-auto text-center">
                        <h1 className="text-4xl lg:text-6xl font-bold mb-6">Get In Touch</h1>
                        <p className="text-gray-400 text-lg leading-relaxed">
                            Have a question or want to collaborate? We'd love to hear from you.
                        </p>
                    </div>
                </div>
            </section>

            <div className="container mx-auto px-4 lg:px-8 py-12 lg:py-16">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
                    {/* Contact Form */}
                    <div className="lg:col-span-2">
                        <div className="border border-gray-200 rounded-lg p-6 lg:p-10">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6">Send Us a Message</h2>

                            {submitted ? (
                                <div className="text-center py-12">
                                    <div className="w-20 h-20 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-6">
                                        <Send size={40} className="text-white" />
                                    </div>
                                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Message Sent!</h3>
                                    <p className="text-gray-600">
                                        Thank you for reaching out. We'll get back to you within 24 hours.
                                    </p>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-900 mb-2">Your Name *</label>
                                            <input
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                required
                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-gray-900"
                                                placeholder="John Doe"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-900 mb-2">Email Address *</label>
                                            <input
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                required
                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-gray-900"
                                                placeholder="john@example.com"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-900 mb-2">Phone Number</label>
                                            <input
                                                type="tel"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-gray-900"
                                                placeholder="+91 98765 43210"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-900 mb-2">Subject *</label>
                                            <select
                                                name="subject"
                                                value={formData.subject}
                                                onChange={handleInputChange}
                                                required
                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-gray-900"
                                            >
                                                <option value="">Select a subject</option>
                                                <option value="general">General Inquiry</option>
                                                <option value="custom">Custom Design Request</option>
                                                <option value="order">Order Support</option>
                                                <option value="collaboration">Collaboration</option>
                                                <option value="wholesale">Wholesale Inquiry</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-900 mb-2">Message *</label>
                                        <textarea
                                            name="message"
                                            value={formData.message}
                                            onChange={handleInputChange}
                                            required
                                            rows="6"
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-gray-900 resize-none"
                                            placeholder="Tell us about your project or inquiry..."
                                        />
                                    </div>

                                    <Button type="submit" variant="primary" size="lg" fullWidth={true}>
                                        <Send size={20} className="mr-2" />
                                        Send Message
                                    </Button>
                                </form>
                            )}
                        </div>
                    </div>

                    {/* Contact Information */}
                    <div className="space-y-6">
                        {/* Contact Details Card */}
                        <div className="border border-gray-200 rounded-lg p-6">
                            <h3 className="text-xl font-bold text-gray-900 mb-6">Contact Information</h3>
                            <div className="space-y-5">
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                                        <Mail className="text-gray-900" size={20} />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900 mb-1">Email</h4>
                                        <a href="mailto:hello@HAUTE.com" className="text-gray-600 hover:text-gray-900">
                                            hello@HAUTE.com
                                        </a>
                                    </div>
                                </div>

                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                                        <Phone className="text-gray-900" size={20} />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900 mb-1">Phone</h4>
                                        <a href="tel:+919876543210" className="text-gray-600 hover:text-gray-900">
                                            +91 98765 43210
                                        </a>
                                    </div>
                                </div>

                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                                        <MapPin className="text-gray-900" size={20} />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900 mb-1">HAUTE</h4>
                                        <p className="text-gray-600">
                                            123 Design Street<br />
                                            Mumbai, Maharashtra 400001<br />
                                            India
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Business Hours */}
                        <div className="bg-gray-900 rounded-lg p-6 text-white">
                            <div className="flex items-center gap-3 mb-4">
                                <Clock size={24} />
                                <h3 className="text-xl font-bold">Business Hours</h3>
                            </div>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-400">Monday - Friday</span>
                                    <span className="font-semibold">9:00 AM - 6:00 PM</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-400">Saturday</span>
                                    <span className="font-semibold">10:00 AM - 4:00 PM</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-400">Sunday</span>
                                    <span className="font-semibold">Closed</span>
                                </div>
                            </div>
                        </div>

                        {/* Social Media */}
                        <div className="border border-gray-200 rounded-lg p-6">
                            <h3 className="text-xl font-bold text-gray-900 mb-4">Follow Us</h3>
                            <div className="flex gap-3">
                                <a href="#" className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-900 hover:text-white transition-colors">
                                    <Instagram size={20} />
                                </a>
                                <a href="#" className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-900 hover:text-white transition-colors">
                                    <Facebook size={20} />
                                </a>
                                <a href="#" className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-900 hover:text-white transition-colors">
                                    <Twitter size={20} />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                {/* FAQ Section */}
                <div className="mt-16">
                    <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Frequently Asked Questions</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="border border-gray-200 rounded-lg p-6">
                            <h4 className="font-bold text-gray-900 mb-2">Do you offer custom designs?</h4>
                            <p className="text-gray-600">
                                Yes! We specialize in creating custom designs. Contact us to discuss your vision.
                            </p>
                        </div>
                        <div className="border border-gray-200 rounded-lg p-6">
                            <h4 className="font-bold text-gray-900 mb-2">What's your turnaround time?</h4>
                            <p className="text-gray-600">
                                Standard orders ship within 3-5 business days. Custom designs take 2-3 weeks.
                            </p>
                        </div>
                        <div className="border border-gray-200 rounded-lg p-6">
                            <h4 className="font-bold text-gray-900 mb-2">Do you ship internationally?</h4>
                            <p className="text-gray-600">
                                Currently, we ship within India. International shipping coming soon!
                            </p>
                        </div>
                        <div className="border border-gray-200 rounded-lg p-6">
                            <h4 className="font-bold text-gray-900 mb-2">Can I visit your HAUTE?</h4>
                            <p className="text-gray-600">
                                Yes! HAUTE visits are by appointment only. Contact us to schedule.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default Contact;
